import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import type { BatchItem, BatchItemState, BatchStatus } from '../../../services/agent.service';
import { batchItemFlags } from '../../../utils/ai-batch-summary.util';

/**
 * Per-invoice step chips and the batch summary, for a bulk AP run.
 *
 * This is what replaced the per-request review card. The card let the accountant edit one
 * invoice inline; this reports what happened to each of twenty and sends them to the AP module
 * to review, which is where the full form, validation and approval routing already live.
 *
 * Implements the state machine from the AI Agent Figma's `Request creator thinking`
 * (node 3147:5163), minus the `denied` state — Deny went with the card, and an unwanted draft
 * is deleted in the AP module instead. See MD_PRD/docs/architecture/design-deltas.md §1.2.
 *
 * `data-testid` attributes are contractual and consumed by the Playwright suite; the class names
 * are not. See Frontend/e2e/pages/ai-agent.page.ts for the full list.
 */
@Component({
  selector: 'app-ai-batch-progress',
  standalone: true,
  imports: [CommonModule, RouterModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './batch-progress.component.html',
  styleUrls: ['./batch-progress.component.scss'],
})
export class AiBatchProgressComponent {
  @Input() batch: BatchStatus | null = null;

  /** Emitted when the user cancels. In-flight invoices still finish. */
  @Output() cancelRequested = new EventEmitter<string>();

  /**
   * Buckets in the order the summary presents them.
   *
   * `created` first because it is the answer to "did it work"; `failed` last because it is the
   * smallest and most actionable. `queued` and `creating` are deliberately absent — they are
   * in-flight states shown by the chips, not outcomes worth a summary bucket.
   */
  readonly buckets: ReadonlyArray<{ state: BatchItemState; label: string }> = [
    { state: 'created', label: 'Created' },
    { state: 'needs-attention', label: 'Needs attention' },
    { state: 'skipped', label: 'Skipped — already exist' },
    { state: 'failed', label: 'Could not create' },
  ];

  get items(): BatchItem[] {
    return this.batch?.items ?? [];
  }

  get isRunning(): boolean {
    return this.batch?.status === 'running';
  }

  /** True once every invoice has reached a terminal state. */
  get isFinished(): boolean {
    if (!this.batch) return false;
    return this.batch.summary.queued === 0 && this.batch.summary.creating === 0;
  }

  /** Invoices still to start or in flight, for the progress line. */
  get inFlightCount(): number {
    if (!this.batch) return 0;
    return this.batch.summary.queued + this.batch.summary.creating;
  }

  get completedCount(): number {
    return this.items.length - this.inFlightCount;
  }

  itemsIn(state: BatchItemState): BatchItem[] {
    return this.items.filter(i => i.state === state);
  }

  /**
   * Count for a bucket, always derived from the items.
   *
   * The Figma's own summary has a hardcoded "Successfully created (2):" label sitting above four
   * chips. Deriving it is the fix. design-deltas.md §3.
   */
  countIn(state: BatchItemState): number {
    return this.itemsIn(state).length;
  }

  /** Buckets with at least one invoice — an empty bucket is not rendered. */
  get visibleBuckets(): ReadonlyArray<{ state: BatchItemState; label: string }> {
    return this.buckets.filter(b => this.countIn(b.state) > 0);
  }

  /** The verb shown on a chip. Queued and creating share a glyph, so wording distinguishes them. */
  verbFor(item: BatchItem): string {
    switch (item.state) {
      case 'queued':
        return 'Create';
      case 'creating':
        return 'Creating';
      case 'created':
        return 'Created';
      case 'skipped':
        return 'Skipped';
      case 'needs-attention':
        return 'Created — check';
      case 'failed':
        return 'Could not create';
    }
  }

  /** Remix icon class per state, matching the design's glyph choices. */
  iconFor(state: BatchItemState): string {
    switch (state) {
      case 'queued':
      case 'creating':
        return 'ri-loader-4-line ai-chip__icon--spin';
      case 'created':
        return 'ri-checkbox-circle-fill';
      case 'skipped':
        return 'ri-skip-forward-fill';
      case 'needs-attention':
        return 'ri-error-warning-fill';
      case 'failed':
        return 'ri-close-circle-fill';
    }
  }

  /** Deep link to the created draft in the AP module — the review surface. */
  linkFor(item: BatchItem): string | null {
    return item.serviceRequestId ? `/accounts-payable/details/${item.serviceRequestId}` : null;
  }

  // --- Reasons ------------------------------------------------------------------------------
  //
  // An invoice routinely has several reasons. The chip has room for one, so the rest sit behind
  // a control that opens them in place. That control has to exist: the previous version wrote
  // "(+3 more)" into the reason text itself, which read as an expander, was not one, and named
  // reasons that were never sent to the browser at all. Reported 2026-09-07.

  private readonly expanded = new Set<string>();

  /** Every reason for one invoice, most actionable first. */
  reasonsFor(item: BatchItem): string[] {
    return batchItemFlags(item);
  }

  /** Reasons beyond the one already on the chip. */
  hiddenReasonCount(item: BatchItem): number {
    return Math.max(0, this.reasonsFor(item).length - 1);
  }

  isExpanded(item: BatchItem): boolean {
    return this.expanded.has(item.id);
  }

  toggleReasons(item: BatchItem): void {
    if (this.expanded.has(item.id)) this.expanded.delete(item.id);
    else this.expanded.add(item.id);
  }

  onCancel(): void {
    if (this.batch) this.cancelRequested.emit(this.batch.batchId);
  }

  trackById(_: number, item: BatchItem): string {
    return item.id;
  }

  trackByState(_: number, bucket: { state: BatchItemState }): string {
    return bucket.state;
  }
}
